import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { Select } from '../Common/Select';
import { DifferentCardButton } from './DifferentCardButton';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { SavedCard } from '@/types/checkout';
import { getLastFourDigits } from '@/utils/paymentParams';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { calculateTotalPrice } from '@/utils/pricing';
import { PayButton } from '../PaymentInputs/PayButton';
import { Input } from '../Common/Input';
import { REQUIRED_CVC_PROVIDERS } from '@/utils/payment';
import { useFormValidation } from '@/hooks/useFormValidation';
import { ValidationRules } from '@/utils/validation';
import SucessIcon from '@/assets/icons/common/checked.svg';
import CreditCardIcon from '@/assets/icons/payments/credit-card-empty.svg';
import { getCardBrandByProvider, getCardBrandIcon } from '@/utils/cardBrandIcon';
import { Email } from '../Common/Email';
import { useTranslate } from '@/hooks/useTranslate';
import { ApprovalText } from '../PaymentInputs/ApprovalText';
import { Popover, PopoverItem } from '../Common/Popover';
import { FontAwesomeIcon } from '../Common/FontAwesomeIcon/FontAwesomeIcon';
import { useRemoveCard } from '@/api/card';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { isEmailValid } from '@/utils/email';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { MAX_LENGTH_CVC } from '@/constants/payment';

export const SavedCardsForm = () => {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodsSettings,
    card: { selectedCard, selectCard },
    customer,
    actions: { runPayment, setIsFormValid, removeCard },
    featureFlags,
    pricing,
  } = context;
  const [cvc, setCvc] = useState<string>('');
  const [isTaxValid, setIsTaxValid] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { t } = useTranslate();
  const { mutate: removeCardMutation } = useRemoveCard();

  const providerByCard = useMemo(() => {
    return (
      (paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD)
        ?.provider as ProviderEnum) || ProviderEnum.ADYEN
    );
  }, [paymentMethodsSettings]);

  const shouldShowCVVField = useMemo(() => {
    return REQUIRED_CVC_PROVIDERS.includes(selectedCard?.paymentProvider as ProviderEnum) && !selectedCard?.isNew;
  }, [selectedCard]);

  const shouldShowEmailField = !shouldHideEmail(featureFlags);

  const { values, errors, touched, setValue, setFieldTouched, validateForm } = useFormValidation(
    {
      cvc: '',
      email: customer?.email || '',
    },
    {
      cvc: [
        ValidationRules.required(t('cvc'), t('fieldErrors.incompleteSecurityCode', { field: t('cvc') })),
        ValidationRules.pattern(/^\d{3,4}$/, t('fieldErrors.incompleteSecurityCode')),
      ],
      email: [
        ValidationRules.required(t('emailAddress'), t('fieldErrors.incomplete', { field: t('emailAddress') })),
        ValidationRules.email(t('fieldErrors.invalidEmail')),
      ],
    }
  );

  useEffect(() => {
    let formIsValid = true;

    if (shouldShowCVVField) {
      const cvcError = errors.cvc;
      formIsValid = !cvcError && values.cvc.trim() !== '';
    }

    if (shouldShowEmailField) {
      const emailError = errors.email;
      formIsValid = formIsValid && !emailError && values.email.trim() !== '';
    }

    setIsFormValid(formIsValid);
  }, [shouldShowCVVField, shouldShowEmailField, errors, values, setIsFormValid]);

  const handlePayment = useCallback(async () => {
    if (shouldShowCVVField) {
      if (!validateForm()) {
        return;
      }
      return await runPayment({ cvc: values.cvc });
    } else {
      return await runPayment({ cvc });
    }
  }, [cvc, values.cvc, runPayment, shouldShowCVVField, validateForm]);

  const handleCvcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnlyRegex = /\D/g;
    const value = e.target.value.replace(digitsOnlyRegex, '').slice(0, MAX_LENGTH_CVC);
    setCvc(value);
    setValue('cvc', value);
  };

  const isCvcValid = shouldShowCVVField ? !errors.cvc && values.cvc.trim() !== '' : true;
  const isFormValidLocal = useMemo(
    () => isCvcValid && isTaxValid && isEmailValid(customer?.email),
    [isCvcValid, isTaxValid, customer?.email]
  );

  const handleDelete = async (card: SavedCard, event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsPopoverOpen(false);

    if (!card.isNew && card.pspCardIdentifier) {
      await removeCardMutation({
        pspCardIdentifier: card.pspCardIdentifier,
        paymentProvider: card.paymentProvider as ProviderEnum,
      });
    }

    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.SAVED_CARD_REMOVED, properties as Record<string, unknown>);

    removeCard(card);
  };

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsTaxValid(isValid);
  }, []);

  if (shouldShowCVVField) {
    return (
      <div className="flex flex-col gap-2">
        {!shouldHideEmail(featureFlags) && (
          <div className="w-full">
            <Email />
          </div>
        )}
        <InputTaxComponent onValidate={handleTaxValidationChange} />
        <div className="flex flex-col gap-4 rounded-lg border border-base-border p-3">
          <div className="flex flex-row justify-between w-full items-center">
            <div className="flex flex-row items-center gap-2">
              <img
                className="w-8 h-6"
                src={getCardBrandIcon(getCardBrandByProvider(providerByCard, selectedCard as SavedCard) as string)}
                alt={selectedCard?.brand}
              />
              <div className="text-sm color-base-foreground flex flex-row gap-1">
                ****{' '}
                {getLastFourDigits(
                  (selectedCard?.cardNumber as string) ??
                    selectedCard?.cardLastFourDigits ??
                    (selectedCard?.cardDetails?.last4digits as string)
                )}
              </div>
            </div>
            <div className="flex flex-row items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Popover
                trigger={
                  <div className="flex items-center justify-center min-w-4 min-h-4 p-2">
                    <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" className="text-xs! cursor-pointer" />
                  </div>
                }
                isOpen={isPopoverOpen}
                onOpenChange={setIsPopoverOpen}
              >
                <PopoverItem
                  variant="danger"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    handleDelete(selectedCard as SavedCard, event)
                  }
                >
                  <div className="flex flex-row items-center gap-2">
                    <FontAwesomeIcon icon="fa-regular fa-trash" className="text-xs! cursor-pointer" />
                    {t('delete')}
                  </div>
                </PopoverItem>
              </Popover>
              <img src={SucessIcon} className="w-4 h-4" />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Input
              maxLength={MAX_LENGTH_CVC}
              placeholder={t('cvc')}
              type="text"
              inputMode="numeric"
              value={values.cvc}
              onChange={handleCvcChange}
              onBlur={() => setFieldTouched('cvc')}
              error={touched.cvc ? errors.cvc : undefined}
              className="w-full"
              renderIcon={() => <img src={CreditCardIcon} className="w-4 h-4" />}
            />
          </div>
        </div>
        <div className="flex flex-row items-center w-full">
          <DifferentCardButton />
        </div>
        <div className="flex flex-col">
          <PayButton onClick={handlePayment} price={calculateTotalPrice(pricing)} isDisabled={!isFormValidLocal} />
          <ApprovalText />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {!shouldHideEmail(featureFlags) && (
        <div className="w-full">
          <Email />
        </div>
      )}
      <InputTaxComponent onValidate={handleTaxValidationChange} />
      <Select
        key={selectedCard?.id}
        id={selectedCard?.id as string}
        isSelected={false}
        onSelect={() => selectCard(selectedCard as SavedCard)}
        className="h-14 border-selected-border rounded-lg"
        style={{ backgroundColor: 'rgba(68, 138, 227, 0.10)' }}
      >
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex flex-row items-center gap-2">
            <img
              className="w-8 h-6"
              src={getCardBrandIcon(getCardBrandByProvider(providerByCard, selectedCard as SavedCard) as string)}
              alt={selectedCard?.brand}
            />
            <div className="text-sm color-base-foreground flex flex-row items- gap-1">
              ****{' '}
              {getLastFourDigits(
                (selectedCard?.cardNumber as string) ??
                  selectedCard?.cardLastFourDigits ??
                  (selectedCard?.cardDetails?.last4digits as string)
              )}
            </div>
          </div>
          <div className="flex flex-row items-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-row items-center gap-3">
              <img src={SucessIcon} className="w-4 h-4" />
              <Popover
                trigger={
                  <div className="flex items-center justify-center min-w-4 min-h-4 p-2">
                    <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" className="text-xs! cursor-pointer" />
                  </div>
                }
                isOpen={isPopoverOpen}
                onOpenChange={setIsPopoverOpen}
              >
                <PopoverItem
                  variant="danger"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    handleDelete(selectedCard as SavedCard, event)
                  }
                >
                  <div className="flex flex-row items-center gap-2">
                    <FontAwesomeIcon icon="fa-regular fa-trash" className="text-xs! cursor-pointer" />
                    {t('delete')}
                  </div>
                </PopoverItem>
              </Popover>
            </div>
          </div>
        </div>
      </Select>
      <div className="flex flex-row items-center justify-center w-full">
        <DifferentCardButton />
      </div>
      <div className="flex flex-col">
        <PayButton onClick={handlePayment} price={calculateTotalPrice(pricing)} isDisabled={!isFormValidLocal} />
        <ApprovalText />
      </div>
    </div>
  );
};
